/**
 * Domain enums for Aforium Learning.
 *
 * These are declared as `as const` objects (rather than TypeScript `enum`s) so that:
 *  - values can be iterated at runtime for selects, filters and validation,
 *  - the same literal union types are usable on the API and in the browser,
 *  - the names and values match the Prisma schema enums exactly.
 *
 * IMPORTANT: every enum here has a 1:1 counterpart in `apps/api/prisma/schema.prisma`.
 * Changing one without the other is a breaking contract change.
 */
/* -------------------------------------------------------------------------- */
/* Identity & access                                                          */
/* -------------------------------------------------------------------------- */
export const Role = {
    ADMIN: 'ADMIN',
    MENTOR: 'MENTOR',
    INTERN: 'INTERN',
};
export const ROLES = Object.values(Role);
/** Roles that may administer content, interns and projects. */
export const STAFF_ROLES = [Role.ADMIN, Role.MENTOR];
/* -------------------------------------------------------------------------- */
/* Intern lifecycle                                                           */
/* -------------------------------------------------------------------------- */
export const InternStatus = {
    ACTIVE: 'ACTIVE',
    ON_HOLD: 'ON_HOLD',
    COMPLETED: 'COMPLETED',
    GRADUATED: 'GRADUATED',
    INACTIVE: 'INACTIVE',
};
export const INTERN_STATUSES = Object.values(InternStatus);
export const ProjectReadiness = {
    NOT_READY: 'NOT_READY',
    READY: 'READY',
    IN_PROJECT: 'IN_PROJECT',
    GRADUATED: 'GRADUATED',
};
export const PROJECT_READINESS_VALUES = Object.values(ProjectReadiness);
/* -------------------------------------------------------------------------- */
/* Skills                                                                     */
/* -------------------------------------------------------------------------- */
export const SkillLevel = {
    BEGINNER: 'BEGINNER',
    INTERMEDIATE: 'INTERMEDIATE',
    ADVANCED: 'ADVANCED',
    EXPERT: 'EXPERT',
};
export const SKILL_LEVELS = Object.values(SkillLevel);
/** Ordered weakest to strongest; used for comparisons and progress bars. */
export const SKILL_LEVEL_ORDER = [
    SkillLevel.BEGINNER,
    SkillLevel.INTERMEDIATE,
    SkillLevel.ADVANCED,
    SkillLevel.EXPERT,
];
export const SkillCategory = {
    LANGUAGE: 'LANGUAGE',
    FRONTEND: 'FRONTEND',
    BACKEND: 'BACKEND',
    DATABASE: 'DATABASE',
    TOOLING: 'TOOLING',
    QUALITY: 'QUALITY',
    ARCHITECTURE: 'ARCHITECTURE',
    PROFESSIONAL: 'PROFESSIONAL',
};
export const SKILL_CATEGORIES = Object.values(SkillCategory);
/** Where a recorded skill level came from, shown as "evidence" in the profile. */
export const SkillEvidenceSource = {
    COURSE_COMPLETION: 'COURSE_COMPLETION',
    QUIZ: 'QUIZ',
    EXERCISE: 'EXERCISE',
    SKIP_ASSESSMENT: 'SKIP_ASSESSMENT',
    FINAL_ASSESSMENT: 'FINAL_ASSESSMENT',
    MENTOR_EVALUATION: 'MENTOR_EVALUATION',
    PROJECT_WORK: 'PROJECT_WORK',
};
export const SKILL_EVIDENCE_SOURCES = Object.values(SkillEvidenceSource);
/* -------------------------------------------------------------------------- */
/* Courses & lessons                                                          */
/* -------------------------------------------------------------------------- */
export const CourseDifficulty = {
    BEGINNER: 'BEGINNER',
    INTERMEDIATE: 'INTERMEDIATE',
    ADVANCED: 'ADVANCED',
};
export const COURSE_DIFFICULTIES = Object.values(CourseDifficulty);
export const LessonStatus = {
    NOT_STARTED: 'NOT_STARTED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
    SKIPPED: 'SKIPPED',
    FAILED: 'FAILED',
};
export const LESSON_STATUSES = Object.values(LessonStatus);
/**
 * How a lesson requirement was satisfied. Progress counts all four as
 * satisfied, but reporting must always keep them distinguishable (see
 * docs/skip-system.md and docs/recap-system.md). RECAP marks a lesson the
 * intern did not study because they completed the whole course through its
 * recap.
 */
export const CompletionMethod = {
    NORMAL: 'NORMAL',
    SKIPPED_PRIOR_KNOWLEDGE: 'SKIPPED_PRIOR_KNOWLEDGE',
    ADMIN_OVERRIDE: 'ADMIN_OVERRIDE',
    RECAP: 'RECAP',
};
export const COMPLETION_METHODS = Object.values(CompletionMethod);
/** Lesson statuses that satisfy progression. */
export const SATISFYING_LESSON_STATUSES = [
    LessonStatus.COMPLETED,
    LessonStatus.SKIPPED,
];
export const CourseProgressStatus = {
    NOT_STARTED: 'NOT_STARTED',
    IN_PROGRESS: 'IN_PROGRESS',
    COMPLETED: 'COMPLETED',
};
export const COURSE_PROGRESS_STATUSES = Object.values(CourseProgressStatus);
export const LessonResourceType = {
    LINK: 'LINK',
    ARTICLE: 'ARTICLE',
    VIDEO: 'VIDEO',
    DOCUMENTATION: 'DOCUMENTATION',
    REPOSITORY: 'REPOSITORY',
    DOWNLOAD: 'DOWNLOAD',
};
export const LESSON_RESOURCE_TYPES = Object.values(LessonResourceType);
/* -------------------------------------------------------------------------- */
/* Exercises & submissions                                                    */
/* -------------------------------------------------------------------------- */
export const ExerciseType = {
    QUIZ: 'QUIZ',
    CODE: 'CODE',
    DEBUGGING: 'DEBUGGING',
    ARCHITECTURE: 'ARCHITECTURE',
    DATABASE: 'DATABASE',
    API: 'API',
    UI: 'UI',
    SHORT_ANSWER: 'SHORT_ANSWER',
    PROJECT_TASK: 'PROJECT_TASK',
};
export const EXERCISE_TYPES = Object.values(ExerciseType);
/**
 * How an exercise's code runs in the learner's browser.
 *
 * `NONE` covers exercises answered in writing, and exercises that need
 * infrastructure a browser sandbox cannot honestly provide — a Node server or a
 * real database. Everything else runs client-side in an isolated sandbox.
 */
export const ExerciseRuntime = {
    NONE: 'NONE',
    JAVASCRIPT: 'JAVASCRIPT',
    TYPESCRIPT: 'TYPESCRIPT',
    HTML: 'HTML',
    REACT: 'REACT',
};
export const EXERCISE_RUNTIMES = Object.values(ExerciseRuntime);
export const ExerciseDifficulty = {
    EASY: 'EASY',
    MEDIUM: 'MEDIUM',
    HARD: 'HARD',
};
export const EXERCISE_DIFFICULTIES = Object.values(ExerciseDifficulty);
export const SubmissionStatus = {
    DRAFT: 'DRAFT',
    SUBMITTED: 'SUBMITTED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    APPROVED: 'APPROVED',
    NEEDS_CHANGES: 'NEEDS_CHANGES',
    REJECTED: 'REJECTED',
};
export const SUBMISSION_STATUSES = Object.values(SubmissionStatus);
/** Submission states that still require mentor attention. */
export const PENDING_REVIEW_STATUSES = [
    SubmissionStatus.SUBMITTED,
    SubmissionStatus.UNDER_REVIEW,
];
export const FeedbackCategory = {
    GENERAL: 'GENERAL',
    CODE_QUALITY: 'CODE_QUALITY',
    LOGIC: 'LOGIC',
    ARCHITECTURE: 'ARCHITECTURE',
    UI: 'UI',
    BACKEND: 'BACKEND',
    TESTING: 'TESTING',
    PROBLEM_SOLVING: 'PROBLEM_SOLVING',
};
export const FEEDBACK_CATEGORIES = Object.values(FeedbackCategory);
/* -------------------------------------------------------------------------- */
/* Quizzes & assessments                                                      */
/* -------------------------------------------------------------------------- */
export const QuestionType = {
    MULTIPLE_CHOICE: 'MULTIPLE_CHOICE',
    MULTI_SELECT: 'MULTI_SELECT',
    TRUE_FALSE: 'TRUE_FALSE',
    SHORT_ANSWER: 'SHORT_ANSWER',
};
export const QUESTION_TYPES = Object.values(QuestionType);
export const AttemptStatus = {
    IN_PROGRESS: 'IN_PROGRESS',
    PASSED: 'PASSED',
    FAILED: 'FAILED',
    EXPIRED: 'EXPIRED',
    ABANDONED: 'ABANDONED',
};
export const ATTEMPT_STATUSES = Object.values(AttemptStatus);
export const AssessmentType = {
    FINAL: 'FINAL',
    COURSE: 'COURSE',
    PLACEMENT: 'PLACEMENT',
    /** The quiz at the end of a course recap; passing it completes the course. */
    RECAP: 'RECAP',
};
export const ASSESSMENT_TYPES = Object.values(AssessmentType);
/* -------------------------------------------------------------------------- */
/* Prior knowledge / skip system                                              */
/* -------------------------------------------------------------------------- */
export const SkipRequestStatus = {
    PENDING: 'PENDING',
    ASSESSMENT_IN_PROGRESS: 'ASSESSMENT_IN_PROGRESS',
    APPROVED: 'APPROVED',
    REJECTED: 'REJECTED',
    CANCELLED: 'CANCELLED',
};
export const SKIP_REQUEST_STATUSES = Object.values(SkipRequestStatus);
/** Why a lesson ended up skipped. */
export const SkipReason = {
    PRIOR_KNOWLEDGE: 'PRIOR_KNOWLEDGE',
    ADMIN_OVERRIDE: 'ADMIN_OVERRIDE',
};
export const SKIP_REASONS = Object.values(SkipReason);
/* -------------------------------------------------------------------------- */
/* Achievements & certificates                                                */
/* -------------------------------------------------------------------------- */
export const AchievementTier = {
    BRONZE: 'BRONZE',
    SILVER: 'SILVER',
    GOLD: 'GOLD',
    PLATINUM: 'PLATINUM',
};
export const ACHIEVEMENT_TIERS = Object.values(AchievementTier);
export const AchievementCategory = {
    LEARNING: 'LEARNING',
    ASSESSMENT: 'ASSESSMENT',
    CONSISTENCY: 'CONSISTENCY',
    PROJECT: 'PROJECT',
    MILESTONE: 'MILESTONE',
};
export const ACHIEVEMENT_CATEGORIES = Object.values(AchievementCategory);
export const CertificateStatus = {
    ISSUED: 'ISSUED',
    REVOKED: 'REVOKED',
};
export const CERTIFICATE_STATUSES = Object.values(CertificateStatus);
/* -------------------------------------------------------------------------- */
/* Projects & tasks                                                           */
/* -------------------------------------------------------------------------- */
export const ProjectStatus = {
    PLANNING: 'PLANNING',
    IN_PROGRESS: 'IN_PROGRESS',
    ON_HOLD: 'ON_HOLD',
    COMPLETED: 'COMPLETED',
    CANCELLED: 'CANCELLED',
};
export const PROJECT_STATUSES = Object.values(ProjectStatus);
export const TaskStatus = {
    TODO: 'TODO',
    IN_PROGRESS: 'IN_PROGRESS',
    IN_REVIEW: 'IN_REVIEW',
    CHANGES_REQUESTED: 'CHANGES_REQUESTED',
    APPROVED: 'APPROVED',
    DONE: 'DONE',
    BLOCKED: 'BLOCKED',
};
export const TASK_STATUSES = Object.values(TaskStatus);
/** Board column order for the project task board. */
export const TASK_BOARD_COLUMNS = [
    TaskStatus.TODO,
    TaskStatus.IN_PROGRESS,
    TaskStatus.IN_REVIEW,
    TaskStatus.CHANGES_REQUESTED,
    TaskStatus.APPROVED,
    TaskStatus.DONE,
    TaskStatus.BLOCKED,
];
export const TaskPriority = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
    URGENT: 'URGENT',
};
export const TASK_PRIORITIES = Object.values(TaskPriority);
export const ProjectMemberRole = {
    LEAD: 'LEAD',
    DEVELOPER: 'DEVELOPER',
    REVIEWER: 'REVIEWER',
    OBSERVER: 'OBSERVER',
};
export const PROJECT_MEMBER_ROLES = Object.values(ProjectMemberRole);
export const TaskSubmissionStatus = {
    SUBMITTED: 'SUBMITTED',
    UNDER_REVIEW: 'UNDER_REVIEW',
    CHANGES_REQUESTED: 'CHANGES_REQUESTED',
    APPROVED: 'APPROVED',
};
export const TASK_SUBMISSION_STATUSES = Object.values(TaskSubmissionStatus);
/* -------------------------------------------------------------------------- */
/* Business management (clients, money)                                       */
/* -------------------------------------------------------------------------- */
export const ClientStatus = {
    PROSPECT: 'PROSPECT',
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
    ARCHIVED: 'ARCHIVED',
};
export const CLIENT_STATUSES = Object.values(ClientStatus);
export const ExpenseCategory = {
    SOFTWARE: 'SOFTWARE',
    HOSTING: 'HOSTING',
    DOMAIN: 'DOMAIN',
    MARKETING: 'MARKETING',
    EMPLOYEE_COST: 'EMPLOYEE_COST',
    EQUIPMENT: 'EQUIPMENT',
    OTHER: 'OTHER',
};
export const EXPENSE_CATEGORIES = Object.values(ExpenseCategory);
export const RevenuePaymentStatus = {
    PENDING: 'PENDING',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    OVERDUE: 'OVERDUE',
    CANCELLED: 'CANCELLED',
};
export const REVENUE_PAYMENT_STATUSES = Object.values(RevenuePaymentStatus);
export const InvoiceStatus = {
    DRAFT: 'DRAFT',
    SENT: 'SENT',
    PARTIALLY_PAID: 'PARTIALLY_PAID',
    PAID: 'PAID',
    OVERDUE: 'OVERDUE',
    CANCELLED: 'CANCELLED',
};
export const INVOICE_STATUSES = Object.values(InvoiceStatus);
/** Invoice states that count toward outstanding receivables. */
export const OUTSTANDING_INVOICE_STATUSES = [
    InvoiceStatus.SENT,
    InvoiceStatus.PARTIALLY_PAID,
    InvoiceStatus.OVERDUE,
];
export const PaymentMethod = {
    BANK_TRANSFER: 'BANK_TRANSFER',
    CARD: 'CARD',
    CASH: 'CASH',
    PAYPAL: 'PAYPAL',
    STRIPE: 'STRIPE',
    OTHER: 'OTHER',
};
export const PAYMENT_METHODS = Object.values(PaymentMethod);
/* -------------------------------------------------------------------------- */
/* Notifications & audit                                                      */
/* -------------------------------------------------------------------------- */
export const NotificationType = {
    EXERCISE_REVIEWED: 'EXERCISE_REVIEWED',
    FEEDBACK_RECEIVED: 'FEEDBACK_RECEIVED',
    ASSESSMENT_AVAILABLE: 'ASSESSMENT_AVAILABLE',
    COURSE_UNLOCKED: 'COURSE_UNLOCKED',
    PROJECT_ASSIGNED: 'PROJECT_ASSIGNED',
    TASK_ASSIGNED: 'TASK_ASSIGNED',
    TASK_REVIEW_REQUESTED: 'TASK_REVIEW_REQUESTED',
    EXERCISE_SUBMITTED: 'EXERCISE_SUBMITTED',
    REVIEW_REQUESTED: 'REVIEW_REQUESTED',
    COURSE_COMPLETED: 'COURSE_COMPLETED',
    FINAL_ASSESSMENT_PASSED: 'FINAL_ASSESSMENT_PASSED',
    PROJECT_READY: 'PROJECT_READY',
    PROJECT_TASK_COMPLETED: 'PROJECT_TASK_COMPLETED',
    ACHIEVEMENT_UNLOCKED: 'ACHIEVEMENT_UNLOCKED',
    CERTIFICATE_ISSUED: 'CERTIFICATE_ISSUED',
    SYSTEM: 'SYSTEM',
};
export const NOTIFICATION_TYPES = Object.values(NotificationType);
export const ActivityType = {
    USER_LOGIN: 'USER_LOGIN',
    USER_LOGOUT: 'USER_LOGOUT',
    LESSON_STARTED: 'LESSON_STARTED',
    LESSON_COMPLETED: 'LESSON_COMPLETED',
    LESSON_SKIPPED: 'LESSON_SKIPPED',
    SKIP_ASSESSMENT_STARTED: 'SKIP_ASSESSMENT_STARTED',
    SKIP_ASSESSMENT_PASSED: 'SKIP_ASSESSMENT_PASSED',
    SKIP_ASSESSMENT_FAILED: 'SKIP_ASSESSMENT_FAILED',
    QUIZ_COMPLETED: 'QUIZ_COMPLETED',
    EXERCISE_SUBMITTED: 'EXERCISE_SUBMITTED',
    EXERCISE_REVIEWED: 'EXERCISE_REVIEWED',
    COURSE_STARTED: 'COURSE_STARTED',
    COURSE_COMPLETED: 'COURSE_COMPLETED',
    FINAL_ASSESSMENT_STARTED: 'FINAL_ASSESSMENT_STARTED',
    FINAL_ASSESSMENT_PASSED: 'FINAL_ASSESSMENT_PASSED',
    FINAL_ASSESSMENT_FAILED: 'FINAL_ASSESSMENT_FAILED',
    FEEDBACK_RECEIVED: 'FEEDBACK_RECEIVED',
    ACHIEVEMENT_UNLOCKED: 'ACHIEVEMENT_UNLOCKED',
    CERTIFICATE_ISSUED: 'CERTIFICATE_ISSUED',
    PROJECT_ASSIGNED: 'PROJECT_ASSIGNED',
    TASK_ASSIGNED: 'TASK_ASSIGNED',
    TASK_SUBMITTED: 'TASK_SUBMITTED',
    TASK_COMPLETED: 'TASK_COMPLETED',
    READINESS_CHANGED: 'READINESS_CHANGED',
    ADMIN_OVERRIDE: 'ADMIN_OVERRIDE',
};
export const ACTIVITY_TYPES = Object.values(ActivityType);
/** Audit-log actions for administrative changes (spec section 70). */
export const AuditAction = {
    CREATE: 'CREATE',
    UPDATE: 'UPDATE',
    DELETE: 'DELETE',
    PUBLISH: 'PUBLISH',
    UNPUBLISH: 'UNPUBLISH',
    SCORE_CHANGED: 'SCORE_CHANGED',
    LESSON_OVERRIDE: 'LESSON_OVERRIDE',
    REQUIREMENT_CHANGED: 'REQUIREMENT_CHANGED',
    SUBMISSION_REVIEWED: 'SUBMISSION_REVIEWED',
    READINESS_CHANGED: 'READINESS_CHANGED',
    ROLE_CHANGED: 'ROLE_CHANGED',
    INVOICE_MODIFIED: 'INVOICE_MODIFIED',
    CERTIFICATE_ISSUED: 'CERTIFICATE_ISSUED',
    CERTIFICATE_REVOKED: 'CERTIFICATE_REVOKED',
};
export const AUDIT_ACTIONS = Object.values(AuditAction);
//# sourceMappingURL=enums.js.map